import { useState } from "react";
import { useTranslation } from "react-i18next";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { type DraftStatus, draftStatusLabel, paginatedCount } from "@/lib/coordinator";
import type { CoordinatorDraft, ModeratorsCoordinatorDraftsListParams } from "@/services/moderators/generated/model";
import {
  useModeratorsCoordinatorDraftsList,
  useModeratorsCoordinatorStatusRetrieve,
} from "@/services/moderators/generated/moderators-coordinator/moderators-coordinator";
import CoordinatorDraftCard from "./CoordinatorDraftCard";
import CoordinatorStallPanel from "./CoordinatorStallPanel";
import "./ModeratorPage.css";
import "./Coordinator.css";

type StatusFilter = DraftStatus | "all";
const STATUS_FILTERS: StatusFilter[] = ["pending", "approved", "rejected", "all"];
const PAGE_SIZE = 20;

/**
 * WP2.6 — coordinator approval queue. Every outgoing follow-up / reminder the
 * coordinator drafts waits here until a moderator approves or rejects it.
 */
const ModeratorCoordinatorPage = () => (
  <ModeratorLayout>
    <CoordinatorQueue />
  </ModeratorLayout>
);

const CoordinatorQueue = () => {
  const { t } = useTranslation(["deals", "common"]);
  const [status, setStatus] = useState<StatusFilter>("pending");
  const [page, setPage] = useState(1);
  const params: ModeratorsCoordinatorDraftsListParams = { status, page, page_size: PAGE_SIZE };
  const { data, isLoading, error, refetch, isFetching } = useModeratorsCoordinatorDraftsList(params, {
    query: { staleTime: 0, refetchOnMount: true },
  });
  const drafts = unwrapPaginated<CoordinatorDraft>(data);
  const count = paginatedCount(data);
  const { data: coordStatus } = useModeratorsCoordinatorStatusRetrieve();
  const defaultReviewMinutes = coordStatus?.defaultReviewMinutes ?? 5;

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("coordinator.queue.title")}</h1>
          <p className="mod-page__subtitle">{t("coordinator.queue.subtitle")}</p>
        </div>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          disabled={isFetching}
          onClick={() => refetch()}
        >
          {t("common:actions.refresh")}
        </button>
      </header>

      {coordStatus && !coordStatus.configured ? (
        <div className="coord-panel__banner" role="status">
          {t("coordinator.notConfiguredQueue")}
        </div>
      ) : null}

      <CoordinatorStallPanel status={coordStatus} />

      <section className="mod-page__card">
        <div className="mod-page__toolbar">
          <label className="mod-modal__label">
            {t("coordinator.queue.statusFilter")}
            <select
              className="mod-page__select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
                setPage(1);
              }}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? t("coordinator.queue.all") : draftStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {isLoading ? <p className="mod-page__loading">{t("common:states.loading")}</p> : null}
        {error ? <p className="mod-page__error">{t("coordinator.queue.loadError")}</p> : null}
        {!isLoading && !error && drafts.length === 0 ? (
          <p className="mod-page__empty">{t("coordinator.queue.empty")}</p>
        ) : null}
        {drafts.map((d) => (
          <CoordinatorDraftCard key={d.id} draft={d} defaultReviewMinutes={defaultReviewMinutes} showDeal />
        ))}

        {count > PAGE_SIZE ? (
          <div className="mod-page__pagination">
            <button
              type="button"
              className="mod-page__btn mod-page__btn--ghost"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              {t("common:actions.previous")}
            </button>
            <span>{t("coordinator.queue.page", { page, pages: Math.ceil(count / PAGE_SIZE) })}</span>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--ghost"
              disabled={page * PAGE_SIZE >= count}
              onClick={() => setPage(page + 1)}
            >
              {t("common:actions.next")}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default ModeratorCoordinatorPage;
